import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEvents, deleteEvent } from '../api/events';
import EventCard from '../components/EventCard';
import { useAuth } from '../contexts/AuthContext';
import { getWishes, createWish, deleteWish } from '../api/wishes';
import ConfirmModal from '../components/ConfirmModal';
import Pagination from '../components/Pagination';

function EventList() {
  const { isAuthenticated, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wishMap, setWishMap] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce: wait 300ms after the user stops typing
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 and fetch when search query changes
  useEffect(() => {
    setCurrentPage(1);
    fetchEvents(1, debouncedQuery);
  }, [debouncedQuery]);

  useEffect(() => {
    fetchEvents(currentPage, debouncedQuery);
  }, [currentPage]);

  useEffect(() => {
    if (isAuthenticated) {
      const token = localStorage.getItem('auth_token');
      getWishes(token).then(data => {
        const map = {};
        (data.wishes || []).forEach(w => { map[w.event_id] = w.id; });
        setWishMap(map);
      });
    }
  }, [isAuthenticated]);

  const fetchEvents = async (page, query = '') => {
    try {
      setLoading(true);
      const data = await getEvents(page, query);
      setEvents(data.events || []);
      if (data.meta) {
        setCurrentPage(data.meta.current_page);
        setTotalPages(data.meta.total_pages);
      }
    } catch (err) {
      setError('Failed to load events');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWish = async (eventId) => {
    const token = localStorage.getItem('auth_token');
    const existingWishId = wishMap[eventId];
    try {
      if (existingWishId) {
        await deleteWish(existingWishId, token);
        setWishMap(prev => { const next = { ...prev }; delete next[eventId]; return next; });
      } else {
        const data = await createWish(eventId, token);
        setWishMap(prev => ({ ...prev, [eventId]: data.wish.id }));
      }
    } catch (err) {
      console.error('Failed to toggle wish', err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteEvent(deleteTarget.id);
      setEvents(events.filter(event => event.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setError('Failed to delete event');
      console.error(err);
    }
  };

  if (loading && events.length === 0) return <div className="loading">Loading events...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="event-list">
      {deleteTarget && (
        <ConfirmModal
          title={`Delete "${deleteTarget.title}"?`}
          message="This will permanently delete the event along with all associated wishes and moments. This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      <div className="page-header">
        <h1>Events</h1>
        {isAdmin && (
          <Link to="/events/new" className="btn btn-primary">Create New Event</Link>
        )}
      </div>

      <div className="events-search">
        <input
          type="text"
          placeholder="Search by title..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="events-search__input"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="events-search__clear"
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {events.length === 0 ? (
        <p className="no-events">
          {searchQuery
            ? `No events found for "${searchQuery}".`
            : isAdmin
              ? 'No events found. Create your first event!'
              : 'No events found.'}
        </p>
      ) : (
        <div className="events-grid">
          {events.map(event => (
            <EventCard
              key={event.id}
              event={event}
              onDelete={(id, title) => setDeleteTarget({ id, title })}
              isAuthenticated={isAuthenticated}
              isAdmin={isAdmin}
              wishId={wishMap[event.id] || null}
              onToggleWish={handleToggleWish}
            />
          ))}
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}

export default EventList;
